/* eslint-disable @typescript-eslint/no-explicit-any */
import { useEffect, useRef } from 'react';

interface Props {
  reloadRef: any;
  fetch: () => void;
  enabled: boolean;
}

/**
 * This hook encapsulates the `reloadOnChangeRef`
 * logic and ensures refetch is only triggered after the first load attempt
 *
 * Triggers a fetch when:
 * - `reloadRef` changes (shallow compare)
 * - `enabled` flips from false → true
 */
export function useReloadRef({ enabled, fetch, reloadRef }: Props) {
  const isFirstRender = useRef(true);

  const enabledRef = useRef(enabled);
  const prevReloadRef = useRef(reloadRef);

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }

    const prevEnabled = enabledRef.current;

    const enabledTurnedOn = !prevEnabled && enabled;

    const reloadRefTrigger = reloadRef !== undefined && prevReloadRef.current !== reloadRef;

    const shouldFetch = reloadRefTrigger || enabledTurnedOn;

    enabledRef.current = enabled;
    prevReloadRef.current = reloadRef;

    if (shouldFetch) fetch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reloadRef, enabled]);
}
